import { get } from 'lodash';
import React, { useCallback, useEffect, useMemo } from 'react';

import AgendaJobsAPI from '../../../api/agendaJobs';
import { CustomTable } from '../../../components';
import PaymentScheduleSelect from '../../../components/PaymentScheduleSelect';
import PageFiltersRenderer from '../../../components/shared-ui/PageFiltersRenderer';
import StatusTag from '../../../components/Tag/StatusTag';
import usePaginatedFiltersQuery from '../../../hooks/queries/usePaginatedFiltersQuery';
import useLocationSearchQueryParser from '../../../hooks/useLocationSearchQueryParser';
import useTableSort from '../../../hooks/useTableSort';
import { momentCompanyFormat } from '../../../utils/common';
import { replaceCurrentPageSearchQueryParams } from '../../../utils/queryParams';

const CronJobsSection = props => {
  const { t, location, history, currentCompany } = props;

  const searchQueryParams = useLocationSearchQueryParser(location);

  const { stringTableSort, handleTableSort } = useTableSort(
    searchQueryParams.type === 'future' ? { nextRunAt: 1 } : { lastRunAt: -1 },
  );

  const handleFiltersChange = useCallback(
    filters => {
      replaceCurrentPageSearchQueryParams(history, filters);

      if (searchQueryParams.type !== filters.type) {
        if (filters.type === 'future') {
          handleTableSort('nextRunAt', 'ascend');
        } else {
          handleTableSort('lastRunAt', 'descend');
        }
      }
    },
    [history, searchQueryParams.type, handleTableSort],
  );

  useEffect(() => {
    return () => {
      handleFiltersChange({});
    };
    // eslint-disable-next-line
  }, []);

  const {
    query: cronJobsQuery,
    paginationConfig,
    handlePageChange,
    handlePageSizeChange,
  } = usePaginatedFiltersQuery({
    enabled: !!searchQueryParams.paymentScheduleId,
    placeholderData: { documents: [], totalCount: 0 },
    queryKey: [
      'cronJobs',
      currentCompany._id,
      searchQueryParams.paymentScheduleId,
      searchQueryParams.type,
      stringTableSort,
    ],
    queryFn: () =>
      new AgendaJobsAPI().fetchCronJobs(
        { paymentScheduleId: searchQueryParams.paymentScheduleId, type: searchQueryParams.type },
        paginationConfig.current,
        paginationConfig.pageSize,
        stringTableSort,
      ),
  });

  const TABLE_COLUMNS = useMemo(
    () => [
      {
        sorter: true,
        key: 'name',
        dataIndex: 'name',
        title: t('name'),
        render: name => t(name),
      },
      {
        sorter: true,
        key: 'status',
        dataIndex: 'status',
        title: t('status'),
        render: status => <StatusTag status={status} />,
      },
      {
        sorter: true,
        key: ['data', 'paymentDate'].join(','),
        dataIndex: ['data', 'paymentDate'],
        title: t('paymentDate'),
        render: date => (
          <span style={{ whiteSpace: 'pre' }}>
            {date ? momentCompanyFormat(date, 'YYYY-MM-DD[\n]hh:mm a') : '-'}
          </span>
        ),
      },
      {
        sorter: true,
        defaultSortOrder: searchQueryParams.type !== 'future' ? 'descend' : undefined,
        key: 'lastRunAt',
        dataIndex: 'lastRunAt',
        title: t('lastRunAt'),
        render: date => (
          <span style={{ whiteSpace: 'pre' }}>
            {date ? momentCompanyFormat(date, 'YYYY-MM-DD[\n]hh:mm a') : '-'}
          </span>
        ),
      },
      {
        sorter: true,
        defaultSortOrder: searchQueryParams.type === 'future' ? 'ascend' : undefined,
        key: 'nextRunAt',
        dataIndex: 'nextRunAt',
        title: t('nextRunAt'),
        render: date => (
          <span style={{ whiteSpace: 'pre' }}>
            {date ? momentCompanyFormat(date, 'YYYY-MM-DD[\n]hh:mm a') : '-'}
          </span>
        ),
      },
      {
        sorter: true,
        key: 'failedAt',
        dataIndex: 'failedAt',
        title: t('failedAt'),
        render: date => (
          <span style={{ whiteSpace: 'pre' }}>
            {date ? momentCompanyFormat(date, 'YYYY-MM-DD[\n]hh:mm a') : '-'}
          </span>
        ),
      },
    ],
    [t, searchQueryParams.type],
  );

  return (
    <div>
      <PageFiltersRenderer
        t={t}
        onFiltersChange={handleFiltersChange}
        filters={[
          {
            componentType: 'select',
            label: t('type'),
            name: 'type',
            defaultValue: searchQueryParams.type || 'past',
            value: searchQueryParams.type,
            options: [
              { label: t('future'), value: 'future' },
              { label: t('past'), value: 'past' },
            ],
            responsive: {
              xs: 24,
              md: 8,
              lg: 6,
              xl: 4,
            },
          },
          {
            componentType: 'custom',
            label: t('paymentSchedule'),
            name: 'paymentScheduleId',
            defaultValue: searchQueryParams.paymentScheduleId,
            value: searchQueryParams.paymentScheduleId,
            render: ({ updateFilterValues, disabled, loading }) => (
              <PaymentScheduleSelect
                t={t}
                defaultActiveFirstOption
                disabled={disabled || loading}
                value={searchQueryParams.paymentScheduleId}
                onChange={paymentScheduleId => updateFilterValues({ paymentScheduleId })}
              />
            ),
            responsive: {
              xs: 24,
              md: 8,
              lg: 6,
              xl: 5,
            },
          },
        ]}
      />

      <CustomTable
        t={t}
        asyncSort
        key={searchQueryParams.type}
        loading={cronJobsQuery.isFetching}
        showSearchInput={false}
        columns={TABLE_COLUMNS}
        scroll={{ x: true }}
        dataSource={get(cronJobsQuery.data, 'documents', [])}
        onChange={({ current }, filters, sorters) => {
          handlePageChange(current);
          handleTableSort(sorters?.columnKey, sorters?.order);
        }}
        pagination={{
          pageSize: paginationConfig.pageSize,
          total: paginationConfig.total,
          current: paginationConfig.current,
          onShowSizeChange: handlePageSizeChange,
        }}
      />
    </div>
  );
};

export default CronJobsSection;
